import axios from "axios";
import { Status } from "core/models/error";


export default class ApiClient {
	constructor(baseURL, parentClient = null) {
		// If this is a route client, inherit from parent
		if (parentClient) {
			this.client = parentClient.client;
			this._baseRoute = baseURL;
			this._isRouteClient = true;
		} else {
			this.client = axios.create({
				baseURL,
				timeout: import.meta.env.MODE === "development" ? 0 : 25000, // no timeout or 25 seconds
				headers: {
					"Content-Type": "application/json"
				}
			});
			this._baseRoute = "";
			this._isRouteClient = false;
		}

		// Only set up interceptors for the main client, not route clients
		if (!this._isRouteClient) {
			this._reqInterceptor();
			this._resInterceptor();
		}
	}

	// Create a new client instance with a scoped route
	route(path) {
		const routePath = this._isRouteClient
			? `${this._baseRoute}${path.startsWith("/") ? path : `/${path}`}`
			: path.startsWith("/") ? path : `/${path}`;

		return new ApiClient(routePath, this._isRouteClient ? this : this);
	}

	// Helper method to build the full path
	_buildPath(path) {
		if (!this._isRouteClient) return path;

		// Handle empty string or root path
		if (!path || path === "/") return this._baseRoute;

		// Ensure path starts with / if not empty
		const normalizedPath = path.startsWith("/") ? path : `/${path}`;
		return `${this._baseRoute}${normalizedPath}`;
	}

	_reqInterceptor() {
		this.client.interceptors.request.use(
			async (config) => {
				const token = this._getAuth0AccessToken();
				if (token) config.headers.Authorization = `Bearer ${token}`;
				return config;
			},
			this._handleError
		);
	}

	_getAuth0AccessToken() {
		// Auth0 stores multiple entries in localStorage with a key starting with @@auth0spajs@@
		const keys = Object.keys(localStorage).filter(k => k.startsWith("@@auth0spajs@@"));
		if (!keys || !keys.length) return null;
		for (const key of keys) {
			const raw = localStorage.getItem(key);
			if (!raw) return null;
			try {
				const data = JSON.parse(raw);
				var exp = data?.expiresAt ?? 0;
				if (Date.now() >= new Date(exp * 1000)) continue; // expired
				return data?.body?.access_token ?? null;
			}
			catch {
				continue;
			}
		}
		return null;
	}

	_resInterceptor() {
		this.client.interceptors.response.use(
			this._handleResponse,
			this._handleError
		);
	}

	_handleResponse(response) {
		const pagination = response.headers.get("X-Pagination");

		if (pagination) return ({ values: response.data, pagination: JSON.parse(pagination) });
		else return response.data;
	}

	_handleError(error) {
		let err;
		const response = error.response;

		if (response) {
			const exception = response.data.exception || {};
			err = {
				message: exception.message || error.message || "API Error",
				errors: response.data.errors,
				stack: exception.stackTrace,
				type: Status[response.status]
			};
		} else if (error.request) {
			// Request was made but no response received
			err = {
				message: "Network error - no response received"
			};
		} else {
			// Something else happened
			err = {
				message: error.message || "Unknown error occurred"
			};
		}

		return Promise.reject(err);
	}

	addAuthToken(token) {
		this.client.interceptors.request.use(
			async (config) => {
				config.headers.Authorization = `Bearer ${token}`;
				return config;
			},
			this._handleError
		);
	}

	async get(path = "", config) {
		return await this.client.get(this._buildPath(path), config);
	}

	async post(path = "", data, config) {
		return await this.client.post(this._buildPath(path), data, config);
	}

	async put(path = "", data, config) {
		return await this.client.put(this._buildPath(path), data, config);
	}

	async patch(path = "", data, config) {
		return await this.client.patch(this._buildPath(path), data, config);
	}

	async delete(path = "", config) {
		return await this.client.delete(this._buildPath(path), config);
	}
}